import type { Spacing } from '@frontend/constants';
import type { StrictOmit } from '@frontend/duck-tape';
import { isString, match, P, type StrictExtract } from '@frontend/duck-tape';
import type { ForwardedRef } from '@frontend/react';
import { forwardRef, useMemo, useState, type ReactNode } from '@frontend/react';
import { Icon, ScrollView, Spinner, Text, XStack, YStack } from '@frontend/web-react';
import type { ClassName } from '@frontend/web-utils';
import { applyClass, concatClasses, safeCss } from '@frontend/web-utils';
import type { ModalProps as MModalProps } from '@mantine/core';
import { Modal as MModal, useMantineTheme } from '@mantine/core';
import { useId } from 'react';
import { Title } from '../Texts';

type BorderRadius =
  | 'rounded-2xl'
  | 'rounded-3xl'
  | 'rounded-full'
  | 'rounded-lg'
  | 'rounded-md'
  | 'rounded-none'
  | 'rounded-sm'
  | 'rounded-xl'
  | 'rounded';

export type ModalProps = StrictOmit<MModalProps, 'centered' | 'className' | 'classNames' | 'opened'> & {
  Footer?: ReactNode;
  Header?: ReactNode;
  bodyClassName?: ClassName;
  confirmOnClose?: boolean;
  confirmOnCloseMessage?: string;
  contentBorderRadius?: BorderRadius;
  contentClassName?: ClassName;
  footerClassName?: ClassName;
  fullScreen?: boolean;
  headerClassName?: ClassName;
  headerGap?: 'lg' | 'md' | 'none' | 'sm';
  isForced?: boolean;
  isLoading?: boolean;
  isOpen: boolean;
  padding?: StrictExtract<Spacing, 'lg' | 'md' | 'sm' | 'xl' | 'xs'> | 'none';
  scrollViewContainerClassName?: ClassName;
  stickyFooter?: boolean;
  stickyHeader?: boolean;
  subtitle?: ReactNode;
  titlesAlignment?: 'center' | 'end' | 'start';
};

const getHeaderBottomPaddingClassName = ({ headerGap }: PickRequired<ModalProps, 'headerGap'>) =>
  match(headerGap)
    .with('lg', () => 'pb-lg')
    .with('md', () => 'pb-md')
    .with('sm', () => 'pb-sm')
    .with('none', () => 'pb-none')
    .exhaustive();

export const getModalHeaderProps = ({ headerGap }: PickRequired<ModalProps, 'headerGap'>) =>
  concatClasses('flex flex-col gap-y-sm min-h-none', getHeaderBottomPaddingClassName({ headerGap }));

const Modal = forwardRef(
  (
    {
      bodyClassName,
      children,
      confirmOnClose,
      confirmOnCloseMessage = 'If you close this window, you will lose your progress.',
      contentBorderRadius = 'rounded-lg',
      contentClassName,
      Footer,
      footerClassName,
      fullScreen,
      Header,
      headerClassName,
      headerGap = 'lg',
      isForced,
      // TODO (ethan): Might be better to default this to 'none'
      isLoading,
      isOpen,
      onClose,
      padding = 'md',
      scrollViewContainerClassName = safeCss('p-md'),
      size = '460px',
      stickyFooter = false,
      stickyHeader = false,
      subtitle,
      title,
      titlesAlignment = 'center',
      withCloseButton,
      ...props
    }: ModalProps,
    scrollViewRef: ForwardedRef<HTMLDivElement>,
  ) => {
    const theme = useMantineTheme();
    const titleId = useId();

    const [shake, setShake] = useState(false);
    const onCloseComposed = () => {
      if (isForced) {
        setShake(true);
        setTimeout(() => setShake(false), 250); // reset shake after animation duration
        return;
      }
      if (confirmOnClose) {
        askBeforeClosing();
      } else {
        onClose();
      }
    };

    const askBeforeClosing = () => {
      const didClickOK = window.confirm(confirmOnCloseMessage);
      if (didClickOK) {
        onClose();
      }
    };

    const resolvedScrollViewContainerClassName = useMemo(
      () =>
        // eslint-disable-next-line tailwindcss/no-custom-classname
        concatClasses(
          `overflow-auto`,
          match(padding)
            .with('lg', () => safeCss('p-lg'))
            .with('md', () => safeCss('p-md'))
            .with('sm', () => safeCss('p-sm'))
            .with('xl', () => safeCss('p-xl'))
            .with('xs', () => safeCss('p-xs'))
            .with('none', () => safeCss('p-none'))
            .with(P.nullish, () => null)
            .exhaustive(),
          scrollViewContainerClassName,
        ),
      [scrollViewContainerClassName, padding],
    );

    const headerClasses = stickyHeader ? safeCss('sticky left-none right-none top-none z-10') : '';
    const footerClasses = stickyFooter ? safeCss('sticky left-none right-none bottom-none z-10') : '';
    const useColumnLayout = stickyHeader || stickyFooter;
    const contentLayoutClass = useColumnLayout ? safeCss('flex flex-col') : undefined;
    const bodyLayoutClasses = useColumnLayout ? safeCss('flex flex-col flex-1 min-h-none') : '';
    const scrollViewLayoutClasses = useColumnLayout ? safeCss('flex-1 min-h-none') : '';

    return (
      <MModal.Root centered fullScreen={fullScreen} onClose={onCloseComposed} opened={isOpen} size={size} {...props}>
        <MModal.Overlay {...props.overlayProps} color={theme.colors.neutral[9]} />
        <MModal.Content
          className={concatClasses(applyClass(shake, 'shake-effect'), contentBorderRadius, contentClassName)}
          classNames={contentLayoutClass ? { content: contentLayoutClass } : undefined}
        >
          {Header ? (
            stickyHeader ? (
              <XStack className={headerClasses}>{Header}</XStack>
            ) : (
              Header
            )
          ) : title || subtitle ? (
            <MModal.Header className={concatClasses(getModalHeaderProps({ headerGap }), headerClassName)}>
              <XStack alignItems="center" className="w-full" justifyContent="between">
                {withCloseButton ? <YStack className="size-lg" /> : null}
                {isString(title) ? (
                  <Title id={titleId} textAlign={titlesAlignment} type="h4">
                    {title}
                  </Title>
                ) : (
                  title
                )}
                {withCloseButton ? (
                  <Icon accessibilityLabel="Close" canFocus={false} name="IconX" onClick={onCloseComposed} size="lg" />
                ) : null}
              </XStack>
              {subtitle ? (
                isString(subtitle) ? (
                  <XStack className="w-full">
                    <Text textAlign={titlesAlignment} type="p-sm">
                      {subtitle}
                    </Text>
                  </XStack>
                ) : (
                  subtitle
                )
              ) : null}
            </MModal.Header>
          ) : null}
          <MModal.Body className={concatClasses('p-none', bodyLayoutClasses, bodyClassName)}>
            {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
            <ScrollView
              className={concatClasses(resolvedScrollViewContainerClassName, scrollViewLayoutClasses)}
              ref={scrollViewRef}
            >
              {isLoading ? (
                // eslint-disable-next-line tailwindcss/no-arbitrary-value
                <YStack alignItems="center" className="min-h-[300px]" justifyContent="center">
                  <Spinner />
                </YStack>
              ) : (
                children
              )}
            </ScrollView>
          </MModal.Body>

          {Footer ? (
            stickyFooter ? (
              <XStack className={footerClasses}>{Footer}</XStack>
            ) : (
              <XStack className={footerClassName}>{Footer}</XStack>
            )
          ) : null}
        </MModal.Content>
      </MModal.Root>
    );
  },
);

export { Modal };
